import { createApp } from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import type { Token } from "markdown-it";

import router from "./router";
import App from "./App.vue";
import TDesign from "tdesign-vue-next";

import axios from "axios";

import "tdesign-vue-next/es/style/index.css";
import "./assets/main.css";

import { ReinitTextCache } from "@/text/library.ts";

// Monaco Editor 改为按需动态加载，不在主入口全局导入
// 语言包在代码编辑器组件中根据选中语言动态加载（见 util/monaco.ts）

// md-editor-v3 的 CSS 改为异步加载，避免静态导入导致 vendor-md JS chunk 被预加载
// CSS 在异步组件加载时通过动态 import 注入（见下方 defineAsyncComponent）
// import "md-editor-v3/lib/style.css";

// md-editor-v3 组件改为异步注册，vendor-md chunk 不再阻塞首屏渲染
// config 从子路径导入，避免拉入 MdEditor/MdPreview 组件代码（约 1.8MB）
import { config } from "md-editor-v3/lib/es/config.mjs";
import LinkAttr from "markdown-it-link-attributes";
import MultimdTable from "markdown-it-multimd-table";
import container from "markdown-it-container";

import { shortcodeInlinePlugin, shortcodeColorPlugin } from "@/util/shortcode.ts";
import { MdEditorAsync } from "@/util/md-components.ts";
import MdPreviewLazy from "@/components/markdown/MdPreviewLazy.vue";

import screenfull from "screenfull";

import highlight from "highlight.js";
import "highlight.js/styles/atom-one-dark.css";

import { initEChart } from "@/init/echart.ts";
// 部署后旧 chunk 失效兜底：重新部署会删除旧 hash chunk，停留在旧页面的用户
// 点击导航时动态 import 404，这里全局监听并自动刷新一次（见 init/chunk-reload.ts）
import { initChunkReloadGuard } from "@/init/chunk-reload.ts";

// md-editor-v3 默认从 unpkg CDN 加载 mermaid/katex/echarts/prettier/cropper，
// 这里用空 data URI 覆盖 CDN URL，避免首页产生大量第三方请求。
// 真正的实例通过 util/md-extensions.ts 动态加载后注入 instance 字段。
const EMPTY_JS = "data:text/javascript,void(0);";
const EMPTY_CSS = "data:text/css,";

config({
  editorExtensions: {
    // highlight 和 screenfull 是基础依赖，保持同步加载
    highlight: {
      instance: highlight,
    },
    screenfull: {
      instance: screenfull,
    },
    // 以下扩展的 CDN URL 置空，instance 由 md-extensions.ts 按需注入
    mermaid: {
      js: EMPTY_JS,
    },
    katex: {
      js: EMPTY_JS,
      css: EMPTY_CSS,
    },
    echarts: {
      js: EMPTY_JS,
    },
    prettier: {
      standaloneJs: EMPTY_JS,
      parserMarkdownJs: EMPTY_JS,
    },
    cropper: {
      js: EMPTY_JS,
      css: EMPTY_CSS,
    },
  },
  markdownItConfig(mdit) {
    // 依赖同步后 markdown-it-container / 短代码插件的类型声明与 md-editor-v3 的类型不完全兼容，
    // 这里以 any 桥接（插件签名在运行时保持不变，类型声明差异属于第三方库版本间的漂移）
    mdit.use(container as any, "align-left", {
      render(tokens: Token[], idx: number) {
        return tokens[idx].nesting === 1 ? '<div class="sh-align-left">\n' : "</div>\n";
      },
    });
    mdit.use(container as any, "align-center", {
      render(tokens: Token[], idx: number) {
        return tokens[idx].nesting === 1 ? '<div class="sh-align-center">\n' : "</div>\n";
      },
    });
    mdit.use(container as any, "align-right", {
      render(tokens: Token[], idx: number) {
        return tokens[idx].nesting === 1 ? '<div class="sh-align-right">\n' : "</div>\n";
      },
    });
    mdit.use(shortcodeInlinePlugin as any, {
      renderers: {
        problem: ([key]: string[]) => `<oj-problem key="${key}">${key}</oj-problem>`,
      },
    });
    mdit.use(shortcodeColorPlugin as any, {
      renderers: {
        red: (_: string[], c: string) => `<span class="dida-font-red">${c}</span>`,
        green: (_: string[], c: string) => `<span class="dida-font-green">${c}</span>`,
        blue:  (_: string[], c: string) => `<span class="dida-font-blue">${c}</span>`,
      },
    });
  },
  // 依赖同步后 markdown-it 插件类型与 md-editor-v3 的期望签名存在版本间漂移，
  // 以 any 桥接（运行时行为不变）
  markdownItPlugins(plugins: any[]): any[] {
    return [
      ...plugins,
      {
        type: "linkAttr",
        plugin: LinkAttr,
        options: {
          matcher(href: string) {
            // 如果使用了markdown-it-anchor
            // 应该忽略标题头部的锚点链接
            return !href.startsWith("#");
          },
          attrs: {
            target: "_blank",
          },
        },
      },
      {
        type: "markdown-it-multimd-table",
        plugin: MultimdTable,
        options: {
            multiline:  true,
            rowspan:    true,
            headerless: true,
            multibody:  true,
            autolabel:  true,
        },
      },
    ].map((item) => {
      switch (item.type) {
        case "katex": {
          return {
            ...item,
            options: {
              ...item.options,
              inlineDelimiters: [{ open: "$", close: "$" }],
              blockDelimiters: [
                { open: "$$", close: "$$" },
              ],
            },
          };
        }
        default: {
          return item;
        }
      }
    });
  },
});

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

initEChart();

// chunk 加载失败守卫须在 app 挂载前注册，覆盖初始导航与后续所有动态 import
initChunkReloadGuard(router);

const app = createApp(App);

app.use(pinia);
app.use(router);
app.use(TDesign);

// md-editor-v3 组件改为异步注册，vendor-md chunk 按需加载，不阻塞首屏
// 组件名必须与模板中一致：<md-preview> → "MdPreview"，<md-editor-v3> → "MdEditorV3"
//
// MdPreview 走包装层：它负责在组件创建前注入 katex 实例，并按内容决定是否加载 mermaid
// MdEditor 直接使用异步组件（见 util/md-components.ts）
app.component("MdPreview", MdPreviewLazy);
app.component("MdEditorV3", MdEditorAsync);

app.mount("#app");

// md-editor-v3 重依赖扩展（mermaid/prettier/cropper）改为路由触发后台预热，
// 避免首页加载不相关的大型 chunk。具体触发逻辑见 router/index.ts 的 afterEach。
// 公式渲染所需的 katex 由上面的异步组件加载器保证，与此预热无关。

ReinitTextCache()
  .then(() => {
    console.log("Text cache reinitialized");
  })
  .catch((e) => {
    console.error("Text cache reinitialization failed", e);
  });

export { axios };
